import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE, updateBox, updateComponent, updateHotel } from '../utils/api';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/home.css';
import { getStoredRole } from '../utils/auth';
import { useTranslation } from '../utils/i18n';
import AdminHotelPanel from '../components/AdminPanel';
import UserHotelPanel from '../components/UserPanel';
import MiniHotelMap from '../components/MiniHotelMap';
import BoxReserver from '../components/BoxReserver';


const DEFAULT_BOX_GRID_COLUMNS = 4;
const DEFAULT_BOX_GRID_ROWS = 4;
const CALENDAR_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getStartOfDay = (dateValue) => {
    const date = new Date(dateValue);
    date.setHours(0, 0, 0, 0);
    return date;
};

const getEndOfDay = (dateValue) => {
    const date = new Date(dateValue);
    date.setHours(23, 59, 59, 999);
    return date;
};

const getDateKey = (dateValue) => {
    const date = getStartOfDay(dateValue);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const toDateTimeLocalInputValue = (dateValue) => {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getHotelId = (hotel) => hotel?.DHTHOTid || hotel?.DHTHOT_id || hotel?.hotelId || hotel?.id;
const getHotelName = (hotel) => hotel?.DHTHOT_name || hotel?.name || hotel?.hotelName || hotel?.title;
const getBoxId = (box) => box?.DHTBOX_id || box?.DHTBOXid || box?.boxId || box?.id;
const getComponentId = (component) => component?.DHTCMP_id || component?.DHTCMPid || component?.componentId || component?.id;

const formatCoordinate = (value) => {
    if (value === null || value === undefined || value === '') {
        return 'Unknown';
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
        return String(value);
    }

    return numericValue.toFixed(6);
};

const getReservationBoxId = (reservation) => (
    reservation?.DHTRSV_box_Id ||
    reservation?.DHTBOX_id ||
    reservation?.boxId ||
    reservation?.DHTBOXid
);

const getReservationStartValue = (reservation) => (
    reservation?.DHTRSV_start_Date ||
    reservation?.startTime ||
    reservation?.start_date
);

const getReservationEndValue = (reservation) => (
    reservation?.DHTRSV_end_Date ||
    reservation?.endTime ||
    reservation?.end_date
);

const isReservationActiveOrUpcomingAt = (reservation, dateValue) => {
    const endValue = getReservationEndValue(reservation);

    if (!endValue) {
        return false;
    }

    const reservationEnd = new Date(endValue);
    const targetDate = new Date(dateValue);

    if (Number.isNaN(reservationEnd.getTime()) || Number.isNaN(targetDate.getTime())) {
        return false;
    }

    return reservationEnd > targetDate;
};

const isReservationActiveAt = (reservation, dateValue) => {
    const startValue = getReservationStartValue(reservation);
    const endValue = getReservationEndValue(reservation);

    if (!startValue || !endValue) {
        return false;
    }

    const reservationStart = new Date(startValue);
    const reservationEnd = new Date(endValue);
    const targetDate = new Date(dateValue);

    if (
        Number.isNaN(reservationStart.getTime()) ||
        Number.isNaN(reservationEnd.getTime()) ||
        Number.isNaN(targetDate.getTime())
    ) {
        return false;
    }

    return reservationStart <= targetDate && reservationEnd > targetDate;
};

const isReservationOnDate = (reservation, dateValue) => {
    const startValue = getReservationStartValue(reservation);
    const endValue = getReservationEndValue(reservation);

    if (!startValue || !endValue) {
        return false;
    }

    const reservationStart = new Date(startValue);
    const reservationEnd = new Date(endValue);

    if (Number.isNaN(reservationStart.getTime()) || Number.isNaN(reservationEnd.getTime())) {
        return false;
    }

    return reservationStart <= getEndOfDay(dateValue) && reservationEnd >= getStartOfDay(dateValue);
};

const getReservationTimeRangeForDay = (reservation, dayDate) => {
    const startValue = getReservationStartValue(reservation);
    const endValue = getReservationEndValue(reservation);

    if (!startValue || !endValue) return null;

    const reservationStart = new Date(startValue);
    const reservationEnd = new Date(endValue);
    if (Number.isNaN(reservationStart.getTime()) || Number.isNaN(reservationEnd.getTime())) return null;

    const dayStart = getStartOfDay(dayDate);
    const dayEnd = getEndOfDay(dayDate);

    const clampedStart = reservationStart < dayStart ? dayStart : reservationStart;
    const clampedEnd = reservationEnd > dayEnd ? dayEnd : reservationEnd;

    if (clampedEnd < clampedStart) return null;

    return { clampedStart, clampedEnd };
};

const reservationOverlapsHour = (reservation, dayDate, hour) => {
    const range = getReservationTimeRangeForDay(reservation, dayDate);
    if (!range) return false;

    const hourStart = new Date(dayDate);
    hourStart.setHours(hour, 0, 0, 0);

    const hourEnd = new Date(dayDate);
    hourEnd.setHours(hour + 1, 0, 0, 0);

    // [rangeStart, rangeEnd] overlaps [hourStart, hourEnd)
    return range.clampedStart < hourEnd && range.clampedEnd >= hourStart;
};

const getHourScheduleForDay = ({ reservationsForHotel, dayDate, allBoxes }) => {
    const hours = Array.from({ length: 24 }, (_, h) => h);
    const dayReservations = reservationsForHotel.filter((r) => isReservationOnDate(r, dayDate));

    return hours.map((hour) => {
        const reservationsForHour = dayReservations.filter((r) => reservationOverlapsHour(r, dayDate, hour));

        const reservedBoxIdsSet = new Set(
            reservationsForHour
                .map((r) => String(getReservationBoxId(r)))
                .filter(Boolean)
        );

        const reservedBoxes = allBoxes.filter((b) => reservedBoxIdsSet.has(String(b.DHTBOX_id)));
        const availableBoxes = allBoxes.filter((b) => !reservedBoxIdsSet.has(String(b.DHTBOX_id)));

        return {
            hour,
            reservedCount: reservedBoxes.length,
            availableCount: availableBoxes.length,
            reservedBoxes,
            availableBoxes,
            reservationsForHour
        };
    });
};



const buildCalendarDays = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstCalendarDay = new Date(year, month, 1 - firstOfMonth.getDay());
    const calendarCellCount = Math.max(35, Math.ceil((firstOfMonth.getDay() + daysInMonth) / 7) * 7);

    return Array.from({ length: calendarCellCount }, (_, index) => {
        const date = new Date(firstCalendarDay);
        date.setDate(firstCalendarDay.getDate() + index);

        return {
            date,
            key: getDateKey(date),
            dayNumber: date.getDate(),
            isCurrentMonth: date.getMonth() === month,
            isToday: getDateKey(date) === getDateKey(new Date())
        };
    });
};

const parseBoxPosition = (positionValue) => {
    if (positionValue === null || positionValue === undefined || positionValue === '') {
        return null;
    }

    if (typeof positionValue === 'number' && Number.isFinite(positionValue)) {
        const numericString = String(Math.trunc(positionValue));
        if (/^\d{3}$/.test(numericString)) {
            return {
                cabinet: Number(numericString[0]),
                row: Number(numericString[1]),
                column: Number(numericString[2])
            };
        }

        return { sequence: positionValue };
    }

    const normalizedValue = String(positionValue).trim();
    if (!normalizedValue) {
        return null;
    }

    const cabinetRowColumnMatch = normalizedValue.match(/^(\d)(\d)(\d)$/);
    if (cabinetRowColumnMatch) {
        return {
            cabinet: Number(cabinetRowColumnMatch[1]),
            row: Number(cabinetRowColumnMatch[2]),
            column: Number(cabinetRowColumnMatch[3])
        };
    }

    const rowColumnMatch = normalizedValue.match(/(\d+)\s*[,x/-]\s*(\d+)/i);
    if (rowColumnMatch) {
        return {
            row: Number(rowColumnMatch[1]),
            column: Number(rowColumnMatch[2])
        };
    }

    const labelledMatch = normalizedValue.match(/r(?:ow)?\s*(\d+)\D+c(?:ol)?\s*(\d+)/i);
    if (labelledMatch) {
        return {
            row: Number(labelledMatch[1]),
            column: Number(labelledMatch[2])
        };
    }

    const numericValue = Number(normalizedValue);
    if (Number.isFinite(numericValue)) {
        return { sequence: numericValue };
    }

    const firstNumberMatch = normalizedValue.match(/(\d+)/);
    if (firstNumberMatch) {
        return { sequence: Number(firstNumberMatch[1]) };
    }

    return null;
};

const normalizeHotelBoxes = (boxes) => {
    const parsedBoxes = boxes.map((box, index) => ({
        ...box,
        _index: index,
        _positionMeta: parseBoxPosition(box.DHTBOX_position)
    }));

    const explicitColumns = parsedBoxes
        .map((box) => box._positionMeta?.column)
        .filter((column) => Number.isFinite(column));
    const inferredColumns = explicitColumns.length > 0
        ? Math.max(...explicitColumns)
        : DEFAULT_BOX_GRID_COLUMNS;
    const gridColumns = Math.max(DEFAULT_BOX_GRID_COLUMNS, inferredColumns);

    return parsedBoxes
        .map((box, index) => {
            const positionMeta = box._positionMeta;

            if (positionMeta?.row) {
                const validColumn = (positionMeta?.column && positionMeta.column > 0) ? positionMeta.column : 1;
                return {
                    ...box,
                    _gridCabinet: positionMeta.cabinet || 1,
                    _gridRow: positionMeta.row,
                    _gridColumn: validColumn
                };
            }

            const fallbackSequence = positionMeta?.sequence || index + 1;

            return {
                ...box,
                _gridCabinet: 1,
                _gridRow: Math.floor((fallbackSequence - 1) / gridColumns) + 1,
                _gridColumn: ((fallbackSequence - 1) % gridColumns) + 1,
                _boxColor: box._boxColor
            };
        })
        .sort((a, b) => {
            if (a._gridCabinet !== b._gridCabinet) return a._gridCabinet - b._gridCabinet;
            if (a._gridRow !== b._gridRow) return a._gridRow - b._gridRow;
            if (a._gridColumn !== b._gridColumn) return a._gridColumn - b._gridColumn;
            return a._index - b._index;
        });
};

const mergeLiveStatusIntoBoxes = (boxes, boxStatuses, statusReservedBoxIds = new Set()) => boxes.map((box) => {
    const liveStatus = boxStatuses[String(box.DHTBOX_id)];
    const baseStatusLabel = liveStatus?.statusLabel || getBoxStatusLabel(box.DHTBOX_status_Id);
    const normalizedBaseStatusLabel = String(baseStatusLabel || '').toLowerCase();
    const availabilityStatusLabel = normalizedBaseStatusLabel.includes('reserved')
        ? 'Available'
        : baseStatusLabel;
    const hasStatusReservation = statusReservedBoxIds.has(String(box.DHTBOX_id));
    const statusLabel = hasStatusReservation
        ? 'Reserved'
        : availabilityStatusLabel;

    return {
        ...box,
        _liveStatusLabel: statusLabel,
        _liveStatusToneClass: getStatusToneClass(statusLabel),
        _availabilityStatusLabel: availabilityStatusLabel,
        _availabilityStatusToneClass: getStatusToneClass(availabilityStatusLabel)
    };
});

const getBoxStatusLabel = (statusId) => {
    if (statusId === null || statusId === undefined || statusId === '') {
        return 'Unknown';
    }

    const numericStatus = Number(statusId);
    if (!Number.isNaN(numericStatus)) {
        if (numericStatus === 1) return 'Available';
        if (numericStatus === 2) return 'Reserved';
        if (numericStatus === 3) return 'Occupied';
        if (numericStatus === 4) return 'Offline';
    }

    return `Status ${statusId}`;
};

const getStatusToneClass = (statusLabel) => {
    const normalizedLabel = String(statusLabel || '').toLowerCase();

    if (normalizedLabel.includes('available')) return 'is-available';
    if (normalizedLabel.includes('reserved')) return 'is-reserved';
    if (normalizedLabel.includes('occupied')) return 'is-occupied';
    if (normalizedLabel.includes('offline')) return 'is-offline';

    return 'is-unknown';
};

// Stable per-box color (unique hue per box index)
const BOX_COLOR_PALETTE = [
    '#22c55e', '#3b82f6', '#8b5cf6', '#f97316', '#14b8a6', '#ef4444',
    '#eab308', '#06b6d4', '#a855f7', '#f43f5e', '#84cc16', '#6366f1',
    '#10b981', '#f59e0b', '#60a5fa', '#f472b6'
];

const withBoxColors = (boxes) => {
    const uniqueIds = Array.from(new Set(boxes.map((b) => String(b.DHTBOX_id)))).filter(Boolean);
    const idToColor = new Map();
    uniqueIds.forEach((id, idx) => {
        idToColor.set(id, BOX_COLOR_PALETTE[idx % BOX_COLOR_PALETTE.length]);
    });

    return boxes.map((box) => ({
        ...box,
        _boxColor: idToColor.get(String(box.DHTBOX_id)) || BOX_COLOR_PALETTE[0]
    }));
};

const HotelPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [hotel, setHotel] = useState(null);
    const [hotels, setHotels] = useState([]);
    const [weather, setWeather] = useState(null);
    const [boxes, setBoxes] = useState([]);
    const [boxStatuses, setBoxStatuses] = useState({});
    const [reservations, setReservations] = useState([]);
    const [currentTime, setCurrentTime] = useState(() => new Date());
    const [availabilityCalendarMonth, setAvailabilityCalendarMonth] = useState(() => getStartOfDay(new Date()));
    const [selectedAvailabilityDay, setSelectedAvailabilityDay] = useState(null);
    const [boxReservationSelection, setBoxReservationSelection] = useState({
        startDate: null,
        endDate: null,
        isDragging: false
    });

    // Calendar box filter (per box id)
    const [selectedCalendarBoxIds, setSelectedCalendarBoxIds] = useState(() => new Set());


    const [components, setComponents] = useState([]);
    const [editingHotelId, setEditingHotelId] = useState(null);
    const [editingBoxId, setEditingBoxId] = useState(null);
    const [editingComponentId, setEditingComponentId] = useState(null);
    const [hotelEditForm, setHotelEditForm] = useState({});
    const [boxEditForm, setBoxEditForm] = useState({});
    const [componentEditForm, setComponentEditForm] = useState({});
    const [editSubmitState, setEditSubmitState] = useState({
        loading: false,
        error: '',
        success: ''
    });

    const [showHotelsWindow, setShowHotelsWindow] = useState(false);
    const [showComponentsWindow, setShowComponentsWindow] = useState(false);
    const [showFlightPlanWindow, setShowFlightPlanWindow] = useState(false);
    const [showAvailabilityCalendar, setShowAvailabilityCalendar] = useState(false);
    const [selectedBoxForDetail, setSelectedBoxForDetail] = useState(null);
    const [showBoxDetailModal, setShowBoxDetailModal] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const role = getStoredRole();
    const { t } = useTranslation();
    const selectedHotelName = useMemo(() => {
        const currentHotelName = getHotelName(hotel);
        if (currentHotelName) {
            return currentHotelName;
        }

        const matchedHotel = hotels.find((hotelOption) => String(getHotelId(hotelOption)) === String(id));
        return getHotelName(matchedHotel) || `Hotel ${id}`;
    }, [hotel, hotels, id]);

    useEffect(() => {
        document.body.classList.add('hotel-dashboard-compact-view');

        return () => {
            document.body.classList.remove('hotel-dashboard-compact-view');
            document.body.classList.remove('hotel-dashboard-sidebar-collapsed');
        };
    }, []);

    useEffect(() => {
        document.body.classList.toggle('hotel-dashboard-sidebar-collapsed', isSidebarCollapsed);

        return () => {
            document.body.classList.remove('hotel-dashboard-sidebar-collapsed');
        };
    }, [isSidebarCollapsed]);

    useEffect(() => {
        const selectedHotelId = sessionStorage.getItem('selectedHotelId');
        if (selectedHotelId && String(selectedHotelId) !== String(id)) {
            navigate('/map');
        }
    }, [id, navigate]);

    useEffect(() => {
        const timerId = setInterval(() => {
            setCurrentTime(new Date());
        }, 30000);

        return () => {
            clearInterval(timerId);
        };
    }, []);

    const fetchReservations = useCallback(async () => {
        const reservationsRes = await fetch(`${API_BASE}/reservations`);
        if (!reservationsRes.ok) {
            console.error('Failed to fetch reservations');
            return;
        }

        const reservationsData = await reservationsRes.json();
        setReservations(Array.isArray(reservationsData) ? reservationsData : []);
    }, []);

    const fetchHotelData = useCallback(async () => {
        try {
            const selectedHotelId = sessionStorage.getItem('selectedHotelId');
            if (selectedHotelId && String(selectedHotelId) !== String(id)) {
                throw new Error('Direct hotel URL access is not allowed. Select a hotel from the map.');
            }

            const hotelsRes = await fetch(`${API_BASE}/hotels`);
            if (hotelsRes.ok) {
                const hotelsData = await hotelsRes.json();
                setHotels(hotelsData);
            }

            // Fetch Hotel Info
            const hotelRes = await fetch(`${API_BASE}/hotels/${id}`);
            if (!hotelRes.ok) throw new Error('Hotel not found');
            const hotelData = await hotelRes.json();
            setHotel(hotelData);

            // Fetch Weather Info
            try {
                const weatherRes = await fetch(`${API_BASE}/weather/${id}`);
                if (weatherRes.ok) {
                    const weatherData = await weatherRes.json();
                    setWeather(weatherData);
                }
            } catch (wErr) {
                console.error("Weather fetch failed:", wErr);
            }

            // Fetch all boxes and filter for this hotel
            const boxesRes = await fetch(`${API_BASE}/hotel-box/${id}`);
            if (boxesRes.ok) {
                const hotelBoxes = await boxesRes.json();
                setBoxes(Array.isArray(hotelBoxes) ? hotelBoxes : []);
            }

            // Fetch reservations so the availability calendar can show date availability
            await fetchReservations();

            // Fetch components for this hotel
            const componentsRes = await fetch(`${API_BASE}/hotel-component/${id}`);
            if (componentsRes.ok) {
                const componentsData = await componentsRes.json();
                setComponents(Array.isArray(componentsData) ? componentsData : []);
            }

            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id, fetchReservations]);

    useEffect(() => {
        fetchHotelData();
    }, [fetchHotelData]);

    useEffect(() => {
        if (boxes.length === 0) {
            setBoxStatuses({});
            return;
        }

        let isCancelled = false;

        const fetchBoxStatuses = async () => {
            const statusEntries = await Promise.all(
                boxes.map(async (box) => {
                    const boxId = box.DHTBOX_id;

                    try {
                        const response = await fetch(`${API_BASE}/box-status-data/${boxId}`);
                        if (!response.ok) {
                            throw new Error(`Failed to fetch status for box ${boxId}`);
                        }

                        const data = await response.json();
                        const statusLabel = data?.statusName || getBoxStatusLabel(data?.DHTBST_id);

                        return [
                            String(boxId),
                            {
                                ...data,
                                statusLabel
                            }
                        ];
                    } catch (statusError) {
                        console.error(`Status fetch failed for box ${boxId}:`, statusError);
                        return [
                            String(boxId),
                            {
                                statusLabel: getBoxStatusLabel(box.DHTBOX_status_Id)
                            }
                        ];
                    }
                })
            );

            if (!isCancelled) {
                setBoxStatuses(Object.fromEntries(statusEntries));
            }
        };

        fetchBoxStatuses();

        return () => {
            isCancelled = true;
        };
    }, [boxes]);

    const handleBack = () => {
        navigate('/map');
    };

    const scrollToSection = (sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleHotelSelect = (hotelId) => {
        setShowHotelsWindow(false);
        sessionStorage.setItem('selectedHotelId', String(hotelId));
        navigate(`/hotel/${hotelId}`);
    };

    const resetEditSubmitState = () => {
        setEditSubmitState({ loading: false, error: '', success: '' });
    };

    const startHotelEdit = (hotelOption) => {
        resetEditSubmitState();
        setEditingHotelId(getHotelId(hotelOption));
        setHotelEditForm({
            name: hotelOption.DHTHOT_name || '',
            location: hotelOption.DHTHOT_location || '',
            capacity: hotelOption.DHTHOT_capacity ?? '',
            address: hotelOption.DHTHOT_address || '',
            latitude: hotelOption.DHTHOT_latitude ?? '',
            longitude: hotelOption.DHTHOT_longitude ?? '',
            entryDate: toDateTimeLocalInputValue(hotelOption.DHTHOT_entry_Date)
        });
    };

    const startBoxEdit = (box, event) => {
        event?.stopPropagation();
        resetEditSubmitState();
        const boxId = getBoxId(box);
        setEditingBoxId(boxId);
        setBoxEditForm({
            DHTHOT_id: box.DHTHOT_id ?? id,
            DHTBOX_length_mm: box.DHTBOX_length_mm ?? '',
            DHTBOX_width_mm: box.DHTBOX_width_mm ?? '',
            DHTBOX_height_mm: box.DHTBOX_height_mm ?? '',
            DHTBOX_qr_code: box.DHTBOX_qr_code || '',
            DHTBOX_position: box.DHTBOX_position ?? ''
        });
    };

    const startComponentEdit = (component) => {
        resetEditSubmitState();
        setEditingComponentId(getComponentId(component));
        setComponentEditForm({
            DHTBOX_id: component.DHTBOX_id ?? '',
            component_type: component.component_type || '',
            component_place: component.component_place || '',
            DHTCMP_entry_Date: toDateTimeLocalInputValue(component.DHTCMP_entry_Date)
        });
    };

    const handleHotelEditFieldChange = (event) => {
        const { name, value } = event.target;
        setHotelEditForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleBoxEditFieldChange = (event) => {
        const { name, value } = event.target;
        setBoxEditForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleComponentEditFieldChange = (event) => {
        const { name, value } = event.target;
        setComponentEditForm((prev) => ({ ...prev, [name]: value }));
    };

    const saveHotelEdit = async (event) => {
        event.preventDefault();
        setEditSubmitState({ loading: true, error: '', success: '' });

        try {
            const hotelId = Number(editingHotelId);
            const capacity = Number(hotelEditForm.capacity);
            const latitude = hotelEditForm.latitude === '' ? null : Number(hotelEditForm.latitude);
            const longitude = hotelEditForm.longitude === '' ? null : Number(hotelEditForm.longitude);
            const payload = {
                DHTHOT_id: hotelId,
                DHTHOT_name: String(hotelEditForm.name || '').trim(),
                DHTHOT_location: String(hotelEditForm.location || '').trim(),
                DHTHOT_capacity: capacity,
                DHTHOT_address: String(hotelEditForm.address || '').trim(),
                DHTHOT_latitude: latitude,
                DHTHOT_longitude: longitude,
                ...(hotelEditForm.entryDate ? { DHTHOT_entry_Date: new Date(hotelEditForm.entryDate).toISOString() } : {})
            };

            if (!hotelId || Number.isNaN(hotelId)) throw new Error('Hotel ID is required.');
            if (!payload.DHTHOT_name) throw new Error('Hotel name is required.');
            if (Number.isNaN(capacity) || capacity < 1) throw new Error('Capacity must be at least 1.');
            if (latitude !== null && Number.isNaN(latitude)) throw new Error('Latitude must be a valid number.');
            if (longitude !== null && Number.isNaN(longitude)) throw new Error('Longitude must be a valid number.');

            await updateHotel(hotelId, payload);
            await fetchHotelData();
            setEditingHotelId(null);
            setEditSubmitState({ loading: false, error: '', success: `Hotel ${hotelId} updated.` });
        } catch (editError) {
            setEditSubmitState({ loading: false, error: editError?.message || 'Failed to update hotel.', success: '' });
        }
    };

    const saveBoxEdit = async (event) => {
        event.preventDefault();
        setEditSubmitState({ loading: true, error: '', success: '' });

        try {
            const boxId = Number(editingBoxId);
            const payload = {
                DHTBOX_id: boxId,
                DHTHOT_id: Number(boxEditForm.DHTHOT_id),
                DHTBOX_length_mm: Number(boxEditForm.DHTBOX_length_mm),
                DHTBOX_width_mm: Number(boxEditForm.DHTBOX_width_mm),
                DHTBOX_height_mm: Number(boxEditForm.DHTBOX_height_mm),
                DHTBOX_qr_code: String(boxEditForm.DHTBOX_qr_code || '').trim(),
                DHTBOX_position: Number(boxEditForm.DHTBOX_position)
            };

            if (!boxId || Number.isNaN(boxId)) throw new Error('Box ID is required.');
            if (!payload.DHTBOX_qr_code) throw new Error('QR code is required.');
            for (const key of ['DHTHOT_id', 'DHTBOX_length_mm', 'DHTBOX_width_mm', 'DHTBOX_height_mm', 'DHTBOX_position']) {
                if (Number.isNaN(payload[key])) throw new Error(`${key} must be a valid number.`);
            }

            await updateBox(boxId, payload);
            await fetchHotelData();
            setEditingBoxId(null);
            setEditSubmitState({ loading: false, error: '', success: `Box ${boxId} updated.` });
        } catch (editError) {
            setEditSubmitState({ loading: false, error: editError?.message || 'Failed to update box.', success: '' });
        }
    };

    const saveComponentEdit = async (event) => {
        event.preventDefault();
        setEditSubmitState({ loading: true, error: '', success: '' });

        try {
            const componentId = Number(editingComponentId);
            const linkedBoxId = componentEditForm.DHTBOX_id === '' ? null : Number(componentEditForm.DHTBOX_id);
            const payload = {
                DHTCMP_id: componentId,
                DHTHOT_id: Number(id),
                DHTBOX_id: linkedBoxId,
                component_type: String(componentEditForm.component_type || '').trim(),
                component_place: String(componentEditForm.component_place || '').trim(),
                ...(componentEditForm.DHTCMP_entry_Date ? { DHTCMP_entry_Date: new Date(componentEditForm.DHTCMP_entry_Date).toISOString() } : {})
            };

            if (!componentId || Number.isNaN(componentId)) throw new Error('Component ID is required.');
            if (linkedBoxId !== null && Number.isNaN(linkedBoxId)) throw new Error('Box ID must be a valid number.');
            if (!payload.component_type) throw new Error('Component type is required.');
            if (!payload.component_place) throw new Error('Component place is required.');

            await updateComponent(componentId, payload);
            await fetchHotelData();
            setEditingComponentId(null);
            setEditSubmitState({ loading: false, error: '', success: `Component ${componentId} updated.` });
        } catch (editError) {
            setEditSubmitState({ loading: false, error: editError?.message || 'Failed to update component.', success: '' });
        }
    };

    const handleBoxClick = (box) => {
        setSelectedBoxForDetail(box);
        setBoxReservationSelection({ startDate: null, endDate: null, isDragging: false });
        setShowBoxDetailModal(true);
    };

    const handleCalendarMonthChange = (offset) => {
        setAvailabilityCalendarMonth((currentMonth) => new Date(
            currentMonth.getFullYear(),
            currentMonth.getMonth() + offset,
            1
        ));
    };

    const startBoxCalendarSelection = (event, dayDate) => {
        event.preventDefault();
        const date = getStartOfDay(dayDate);

        setSelectedAvailabilityDay(date);
        setBoxReservationSelection({
            startDate: date,
            endDate: date,
            isDragging: true
        });
    };

    const updateBoxCalendarSelection = (dayDate) => {
        setBoxReservationSelection((currentSelection) => {
            if (!currentSelection.isDragging || !currentSelection.startDate) {
                return currentSelection;
            }

            return {
                ...currentSelection,
                endDate: getStartOfDay(dayDate)
            };
        });
    };

    const finishBoxCalendarSelection = useCallback(() => {
        setBoxReservationSelection((currentSelection) => {
            if (!currentSelection.isDragging) {
                return currentSelection;
            }

            return {
                ...currentSelection,
                isDragging: false
            };
        });
    }, []);

    useEffect(() => {
        if (!boxReservationSelection.isDragging) {
            return undefined;
        }

        window.addEventListener('pointerup', finishBoxCalendarSelection);
        return () => {
            window.removeEventListener('pointerup', finishBoxCalendarSelection);
        };
    }, [boxReservationSelection.isDragging, finishBoxCalendarSelection]);

    const getBoxComponents = (boxId) => {
        return components.filter((component) => String(component.DHTBOX_id) === String(boxId));
    };

    const boxIds = useMemo(
        () => new Set(boxes.map((box) => String(box.DHTBOX_id))),
        [boxes]
    );
    const hotelReservations = useMemo(
        () => reservations.filter((reservation) => boxIds.has(String(getReservationBoxId(reservation)))),
        [reservations, boxIds]
    );
    const statusReservedBoxIds = useMemo(
        () => new Set(
            hotelReservations
                .filter((reservation) => isReservationActiveOrUpcomingAt(reservation, currentTime))
                .map((reservation) => String(getReservationBoxId(reservation)))
                .filter(Boolean)
        ),
        [hotelReservations, currentTime]
    );
    const normalizedBoxes = useMemo(
        () => withBoxColors(mergeLiveStatusIntoBoxes(normalizeHotelBoxes(boxes), boxStatuses, statusReservedBoxIds)),
        [boxes, boxStatuses, statusReservedBoxIds]
    );
    const activeReservationBoxIds = useMemo(
        () => new Set(
            hotelReservations
                .filter((reservation) => isReservationActiveAt(reservation, currentTime))
                .map((reservation) => String(getReservationBoxId(reservation)))
                .filter(Boolean)
        ),
        [hotelReservations, currentTime]
    );
    const availableBoxesNowCount = useMemo(
        () => normalizedBoxes.filter((box) => (
            box._availabilityStatusToneClass === 'is-available' &&
            !activeReservationBoxIds.has(String(box.DHTBOX_id))
        )).length,
        [normalizedBoxes, activeReservationBoxIds]
    );

    // Initialize calendar filter to "all boxes" once boxes are loaded.
    useEffect(() => {
        if (!boxes || boxes.length === 0) return;
        setSelectedCalendarBoxIds(new Set(boxes.map((b) => String(b.DHTBOX_id))));
    }, [boxes]);

    const filteredNormalizedBoxes = useMemo(
        () => normalizedBoxes.filter((b) => selectedCalendarBoxIds.has(String(b.DHTBOX_id))),
        [normalizedBoxes, selectedCalendarBoxIds]
    );

    const selectedBoxForDetailLive = useMemo(
        () => normalizedBoxes.find((box) => String(box.DHTBOX_id) === String(selectedBoxForDetail?.DHTBOX_id)) || selectedBoxForDetail,
        [normalizedBoxes, selectedBoxForDetail]
    );
    const selectedBoxReservations = useMemo(() => {
        if (!selectedBoxForDetailLive?.DHTBOX_id) {
            return [];
        }

        return hotelReservations
            .filter((reservation) => String(getReservationBoxId(reservation)) === String(selectedBoxForDetailLive.DHTBOX_id))
            .sort((firstReservation, secondReservation) => {
                const firstDate = new Date(getReservationStartValue(firstReservation)).getTime();
                const secondDate = new Date(getReservationStartValue(secondReservation)).getTime();
                return firstDate - secondDate;
            });
    }, [hotelReservations, selectedBoxForDetailLive]);
    const selectedBoxAvailability = useMemo(() => {
        const activeReservation = selectedBoxReservations.find((reservation) => isReservationActiveAt(reservation, currentTime));
        const upcomingReservations = selectedBoxReservations.filter((reservation) => isReservationActiveOrUpcomingAt(reservation, currentTime));
        const statusLabel = selectedBoxForDetailLive?._availabilityStatusLabel || selectedBoxForDetailLive?._liveStatusLabel || 'Unknown';
        const isBaseAvailable = String(statusLabel).toLowerCase().includes('available');
        const isAvailableNow = isBaseAvailable && !activeReservation;

        return {
            activeReservation,
            upcomingReservations,
            isAvailableNow,
            statusLabel: activeReservation ? 'Reserved now' : statusLabel
        };
    }, [currentTime, selectedBoxForDetailLive, selectedBoxReservations]);
    const calendarDays = useMemo(
        () => buildCalendarDays(availabilityCalendarMonth),
        [availabilityCalendarMonth]
    );
    const selectedBoxCalendarAvailability = useMemo(() => {
        if (!selectedBoxForDetailLive?.DHTBOX_id) {
            return {};
        }

        const baseStatusLabel = selectedBoxForDetailLive._availabilityStatusLabel || selectedBoxForDetailLive._liveStatusLabel || '';
        const isBaseAvailable = String(baseStatusLabel).toLowerCase().includes('available');

        return calendarDays.reduce((availabilityMap, day) => {
            const reservationsForDay = selectedBoxReservations.filter((reservation) => isReservationOnDate(reservation, day.date));
            const isReserved = reservationsForDay.length > 0;

            availabilityMap[day.key] = {
                isAvailable: isBaseAvailable && !isReserved,
                isReserved,
                reservations: reservationsForDay
            };

            return availabilityMap;
        }, {});
    }, [calendarDays, selectedBoxForDetailLive, selectedBoxReservations]);
    const selectedBoxReservationDateRange = useMemo(() => {
        if (!boxReservationSelection.startDate || !boxReservationSelection.endDate) {
            return null;
        }

        const firstDate = getStartOfDay(boxReservationSelection.startDate);
        const secondDate = getStartOfDay(boxReservationSelection.endDate);
        const startDate = firstDate <= secondDate ? firstDate : secondDate;
        const endDate = firstDate <= secondDate ? secondDate : firstDate;

        return {
            startDate,
            endDate,
            startTime: toDateTimeLocalInputValue(startDate),
            endTime: toDateTimeLocalInputValue(getEndOfDay(endDate))
        };
    }, [boxReservationSelection.startDate, boxReservationSelection.endDate]);
    const filteredAvailableBoxes = useMemo(
        () => filteredNormalizedBoxes.filter((box) => box._availabilityStatusToneClass === 'is-available'),
        [filteredNormalizedBoxes]
    );

    const calendarAvailability = useMemo(() => {
        return calendarDays.reduce((availabilityMap, day) => {
            const reservedBoxIds = new Set(
                hotelReservations
                    .filter((reservation) => isReservationOnDate(reservation, day.date))
                    .map((reservation) => String(getReservationBoxId(reservation)))
            );

            const reservedForDay = filteredNormalizedBoxes.filter((box) => reservedBoxIds.has(String(box.DHTBOX_id)));
            const availableForDay = filteredAvailableBoxes.filter((box) => {
                return !reservedBoxIds.has(String(box.DHTBOX_id));
            });

            availabilityMap[day.key] = {
                availableCount: availableForDay.length,
                reservedCount: reservedForDay.length,
                availableBoxes: availableForDay,
                reservedBoxes: reservedForDay,
                allReserved: filteredNormalizedBoxes.length > 0 && reservedForDay.length === filteredNormalizedBoxes.length
            };

            return availabilityMap;
        }, {});
    }, [filteredAvailableBoxes, calendarDays, hotelReservations, filteredNormalizedBoxes]);

    const todayAvailability = calendarAvailability[getDateKey(new Date())] || {
        availableCount: filteredAvailableBoxes.length,
        reservedCount: 0,
        availableBoxes: filteredAvailableBoxes,
        reservedBoxes: [],
        allReserved: false
    };

    const calendarMonthLabel = availabilityCalendarMonth.toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric'
    });
    const hotelBoxGridColumns = Math.max(
        DEFAULT_BOX_GRID_COLUMNS,
        ...normalizedBoxes.map((box) => box._gridColumn || DEFAULT_BOX_GRID_COLUMNS)
    );
    const hotelBoxGridRows = Math.max(
        DEFAULT_BOX_GRID_ROWS,
        ...normalizedBoxes.map((box) => box._gridRow || DEFAULT_BOX_GRID_ROWS)
    );
    const cabinetEntries = Array.from(
        normalizedBoxes.reduce((map, box) => {
            const cabinetNumber = box._gridCabinet || 1;
            if (!map.has(cabinetNumber)) {
                map.set(cabinetNumber, []);
            }
            map.get(cabinetNumber).push(box);
            return map;
        }, new Map())
    );

    if (loading) return <div className="home-container d-flex align-items-center justify-content-center" style={{ height: '100vh' }}><div className="spinner-border text-light" role="status"></div></div>;

    if (error) return <div className="home-container"><div className="container mt-5"><div className="alert alert-danger">{t('Error')}: {error}</div><button className="btn btn-outline-light" onClick={handleBack}>{t('Back to Map')}</button></div></div>;

    // Helper to get weather icon based on weerlive.nl image/condition
    const getWeatherIcon = (image, samenv) => {
        const img = image?.toLowerCase() || '';
        const cond = samenv?.toLowerCase() || '';
        
        if (img.includes('cloud') || cond.includes('bewolkt')) return 'bi-clouds-fill';
        if (img.includes('regen') || cond.includes('regen')) return 'bi-cloud-rain-fill';
        if (img.includes('sneeuw') || cond.includes('sneeuw')) return 'bi-cloud-snow-fill';
        if (img.includes('onweer') || cond.includes('onweer')) return 'bi-cloud-lightning-rain-fill';
        if (img.includes('zon') || cond.includes('zonnig') || cond.includes('helder')) return 'bi-sun-fill';
        if (img.includes('mist')) return 'bi-cloud-fog-fill';
        return 'bi-thermometer-half';
    };

    return (
        <div className="home-container" style={{ minHeight: '100vh', paddingBottom: '3rem' }}>
            <div className={`hotel-dashboard${isSidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
                <aside className="hotel-sidebar text-white">
                    <div className="hotel-sidebar-brand">
                        <button
                            type="button"
                            className="btn hotel-sidebar-toggle"
                            onClick={() => setIsSidebarCollapsed((previous) => !previous)}
                            aria-label={isSidebarCollapsed ? t('Expand navigation menu') : t('Collapse navigation menu')}
                            aria-expanded={!isSidebarCollapsed}
                        >
                            <i className={`bi ${isSidebarCollapsed ? 'bi-layout-sidebar-inset' : 'bi-layout-sidebar'}`}></i>
                            <span>{isSidebarCollapsed ? t('Expand') : t('Collapse')}</span>
                        </button>
                    </div>

                    <div className="hotel-sidebar-section-title">{t('Quick Navigation')}</div>
                    <div className="hotel-sidebar-nav">
                        <button type="button" className="btn hotel-sidebar-link is-active" onClick={() => scrollToSection('hotel-overview')}>
                            <i className="bi bi-building me-2"></i>
                            <span className="hotel-sidebar-link-label">{t('Overview')}</span>
                        </button>
                        <button type="button" className="btn hotel-sidebar-link" onClick={() => scrollToSection('hotel-tools')}>
                            <i className="bi bi-sliders me-2"></i>
                            <span className="hotel-sidebar-link-label">{role === 'admin' ? t('Admin Tools') : t('Reservation Panel')}</span>
                        </button>
                        <button type="button" className="btn hotel-sidebar-link" onClick={() => scrollToSection('hotel-boxes')}>
                            <i className="bi bi-grid me-2"></i>
                            <span className="hotel-sidebar-link-label">{t('Active Boxes')}</span>
                        </button>
                        <button type="button" className="btn hotel-sidebar-link" onClick={() => setShowComponentsWindow(true)}>
                            <i className="bi bi-cpu me-2"></i>
                            <span className="hotel-sidebar-link-label">{t('Components')}</span>
                        </button>
                        <button type="button" className="btn hotel-sidebar-link" onClick={() => setShowHotelsWindow(true)}>
                            <i className="bi bi-buildings me-2"></i>
                            <span className="hotel-sidebar-link-label">{t('Hotels')}</span>
                        </button>
                        <button type="button" className="btn hotel-sidebar-link" onClick={() => setShowAvailabilityCalendar(true)}>
                            <i className="bi bi-calendar3 me-2"></i>
                            <span className="hotel-sidebar-link-label">{t('Box Availability')}</span>
                        </button>
                    </div>
                </aside>

                <main className="hotel-main">
                    <nav className="navbar navbar-dark home-navbar hotel-main-navbar">
                        <div className="container-fluid px-4">
                            <span className="navbar-brand mb-0 h1 fw-bold fs-4">Drone Hotel</span>
                            <div className="d-flex gap-2">
                                <button className="btn profile-icon-button" onClick={() => navigate('/profile')} aria-label={t('Open profile')}>
                                    <i className="bi bi-person-circle"></i>
                                </button>
                                <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/')}>{t('Home')}</button>
                                <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/map')}>{t('Map')}</button>
                                <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/manual')}>{t('Manual')}</button>
                            </div>
                        </div>
                    </nav>

                    <div className="hotel-main-content">
                        <section id="hotel-overview" className="hotel-section-shell hotel-overview-shell mb-4">
                            <div className="hotel-section-header">
                                <div>
                                    <h1 className="hotel-overview-title fw-bold mb-1 text-white">{hotel?.DHTHOT_name}</h1>
                                    <p className="text-white-50 mb-0">{t('Hotel operations, weather, and live box activity in one dashboard.')}</p>
                                </div>
                                <div className="hotel-overview-map">
                                    <MiniHotelMap hotel={hotel} />
                                </div>
                            </div>

                            <div className="hotel-overview-stats">
                                {weather && (
                                    <div className="hotel-overview-stat">
                                        <div className="hotel-info-badge weather-badge py-3 px-3 shadow-lg w-100 h-100">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="weather-icon-container">
                                                    <i className={`bi ${getWeatherIcon(weather.image, weather.samenv)}`} style={{ fontSize: '2.5rem', color: '#1e293b' }}></i>
                                                </div>
                                                <div className="text-start border-start border-light ps-3">
                                                    <div className="h2 mb-0 fw-bold" style={{ color: '#1e293b' }}>{Math.round(weather.temp || 0)}°C</div>
                                                    <div className="text-capitalize fw-bold text-primary" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>{weather.samenv || t('Weather')}</div>
                                                    <div style={{ fontSize: '0.7rem' }} className="text-muted text-nowrap">
                                                        {weather.lv || 0}% hum | {weather.windkmh || 0}km/h
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {role === 'admin' && (
                                    <div className="hotel-overview-stat">
                                        <div className="hotel-info-badge capacity-badge py-3 px-3 shadow-lg w-100 h-100">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="icon-container">
                                                    <i className="bi bi-box-fill" style={{ fontSize: '2.5rem', color: '#1e293b' }}></i>
                                                </div>
                                                <div className="text-start border-start border-light ps-3">
                                                    <div className="h2 mb-0 fw-bold" style={{ color: '#1e293b' }}>{boxes.length}</div>
                                                    <div className="fw-bold text-primary" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>{t('Total Boxes')}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="hotel-overview-stat">
                                    <div className="hotel-info-badge py-3 px-3 shadow-lg w-100 h-100">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="icon-container">
                                                <i className="bi bi-lightning-charge-fill" style={{ fontSize: '2.5rem', color: '#1e293b' }}></i>
                                            </div>
                                            <div className="text-start border-start border-light ps-3">
                                                <div className="h2 mb-0 fw-bold" style={{ color: '#1e293b' }}>{availableBoxesNowCount}</div>
                                                <div className="fw-bold text-primary" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>{t('Active Boxes')}</div>
                                                <div style={{ fontSize: '0.7rem' }} className="text-muted text-nowrap">{t('Available boxes now')}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="hotel-overview-stat">
                                    <div className="hotel-info-badge py-3 px-3 shadow-lg w-100 h-100">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="icon-container">
                                                <i className="bi bi-person-badge-fill" style={{ fontSize: '2.5rem', color: '#1e293b' }}></i>
                                            </div>
                                            <div className="text-start border-start border-light ps-3">
                                                <div className="h2 mb-0 fw-bold text-uppercase" style={{ color: '#1e293b', fontSize: '1.2rem' }}>
                                                    {role === 'admin' ? 'Admin' : t('User')}
                                                </div>
                                                <div className="fw-bold text-primary" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>{t('Access Level')}</div>
                                                <div style={{ fontSize: '0.7rem' }} className="text-muted text-nowrap">Hotel ID {id}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="hotel-overview-stat">
                                    <div className="hotel-info-badge py-3 px-3 shadow-lg w-100 h-100">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="icon-container">
                                                <i className="bi bi-cpu-fill" style={{ fontSize: '2.5rem', color: '#1e293b' }}></i>
                                            </div>
                                            <div className="text-start border-start border-light ps-3">
                                                <div className="h2 mb-0 fw-bold" style={{ color: '#1e293b' }}>{components.length}</div>
                                                <div className="fw-bold text-primary" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>{t('Components')}</div>
                                                <div style={{ fontSize: '0.7rem' }} className="text-muted text-nowrap">{t('Connected to this hotel')}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section id="hotel-tools" className="hotel-section-shell hotel-tools-shell mb-4">
                            <div className="hotel-section-title-row">
                                <div>
                                    <h3 className="hotel-tools-title text-white mb-1">{role === 'admin' ? t('Control Center') : t('Reservation Center')}</h3>
                                    <p className="hotel-tools-subtitle text-white-50 mb-0">
                                        {role === 'admin' ? t('Inspect and manage boxes from a single workspace.') : t('Reserve and review boxes available in this hotel.')}
                                    </p>
                                </div>
                            </div>

                            {role === 'admin' ? (
                                <AdminHotelPanel boxes={boxes} />
                            ) : (
                                <div className="row g-2">
                                    <div className="col-12">
                                        <UserHotelPanel boxes={boxes} onReservationSuccess={fetchReservations} />
                                    </div>
                                </div>
                            )}


                        </section>



                        <section id="hotel-boxes" className="hotel-section-shell">
                            <div className="mb-4 d-flex align-items-center justify-content-between flex-wrap gap-3">
                                <div>
                                    <h3 className="text-white mb-1">{t('Active Boxes')}</h3>
                                    <p className="text-white-50 mb-0">{t('Live box slots currently connected to this hotel.')}</p>
                                </div>
                                <button className="btn btn-sm btn-outline-light opacity-75" onClick={() => window.location.reload()}>
                                    <i className="bi bi-arrow-clockwise me-1"></i> {t('Refresh Status')}
                                </button>
                            </div>

                            <div className="mb-4">
                                {boxes.length > 0 ? (
                                    <>
                                    <div
                                        className="hotel-box-grid-wrap"
                                        style={{
                                            '--hotel-box-grid-columns': hotelBoxGridColumns,
                                            '--hotel-box-grid-rows': hotelBoxGridRows
                                        }}
                                    >
                                        <div className="hotel-box-cabinets">
                                            {cabinetEntries.map(([cabinetNumber, cabinetBoxes]) => (
                                                <div key={cabinetNumber} className="hotel-box-cabinet">
                                                    <div className="hotel-box-cabinet-title">Kabinet {cabinetNumber}</div>
                                                    <div className="hotel-box-grid">
{cabinetBoxes.map((box) => (
                                                            <div
                                                                key={getBoxId(box)}
                                                                className={`hotel-box-slot ${box._liveStatusToneClass}`}
                                                                style={{
                                                                    gridRow: box._gridRow,
                                                                    gridColumn: box._gridColumn,
                                                                    '--box-accent': box._boxColor
                                                                }}
                                                                onClick={() => handleBoxClick(box)}
                                                                role="button"
                                                                tabIndex={0}
                                                                onKeyPress={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        handleBoxClick(box);
                                                                    }
                                                                }}
                                                            >
                                                                <div className="hotel-box-slot-inner">
                                                                    <div className="hotel-box-slot-label">
                                                                        Box {box.DHTBOX_id}
                                                                    </div>
                                                                    <div className="hotel-box-slot-meta">
                                                                        <span>{box._liveStatusLabel}</span>
                                                                        <div className="hotel-box-position-info">
                                                                            <span className="position-value">Pos {box.DHTBOX_position || `${cabinetNumber}${box._gridRow}${box._gridColumn}`}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    </>
                                ) : (
                                    <div className="col-12">
                                        <div className="text-white opacity-50 p-5 text-center border border-dashed rounded-3" style={{ borderStyle: 'dashed !important' }}>
                                            <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                                            {t('No boxes currently assigned to this location.')}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="text-center">
                                <button className="btn btn-outline-light rounded-pill px-5 py-3 fw-bold shadow transition-all" onClick={handleBack}>
                                    <i className="bi bi-chevron-left me-2"></i>
                                    {t('Return to Map')}
                                </button>
                            </div>
                        </section>
                    </div>
                </main>
            </div>
            {showHotelsWindow && (
                <div className="hotel-switcher-backdrop" onClick={() => setShowHotelsWindow(false)}>
                    <div
                        className="hotel-switcher-window"
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="hotel-switcher-title"
                    >
                        <div className="hotel-switcher-header">
                            <div>
                                <div className="hotel-switcher-eyebrow">Hotels</div>
                                <h2 id="hotel-switcher-title" className="hotel-switcher-title mb-0">Navigate to another hotel</h2>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-light rounded-pill px-3"
                                onClick={() => setShowHotelsWindow(false)}
                            >
                                Close
                            </button>
                        </div>

                        <div className="hotel-switcher-list">
                            {hotels.length > 0 ? (
                                <div className="hotel-switcher-table-wrap">
                                    <table className="hotel-switcher-table">
                                        <thead>
                                            <tr>
                                                <th>Hotel</th>
                                                <th>Latitude</th>
                                                <th>Longitude</th>
                                                <th className="hotel-switcher-action-col">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hotels.map((hotelOption) => {
                                                const hotelOptionId = getHotelId(hotelOption);
                                                const isCurrentHotel = String(hotelOptionId) === String(id);
                                                const isEditingHotel = String(editingHotelId) === String(hotelOptionId);

                                                if (isEditingHotel) {
                                                    return (
                                                        <tr key={hotelOptionId} className={isCurrentHotel ? 'is-current' : ''}>
                                                            <td colSpan="4">
                                                                <form onSubmit={saveHotelEdit} className="hotel-inline-edit-form">
                                                                    <input
                                                                        type="text"
                                                                        name="name"
                                                                        className="hotel-inline-edit-input"
                                                                        value={hotelEditForm.name}
                                                                        onChange={handleHotelEditFieldChange}
                                                                        aria-label="Hotel name"
                                                                        required
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        name="capacity"
                                                                        className="hotel-inline-edit-input"
                                                                        value={hotelEditForm.capacity}
                                                                        onChange={handleHotelEditFieldChange}
                                                                        aria-label="Hotel capacity"
                                                                        required
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        step="any"
                                                                        name="latitude"
                                                                        className="hotel-inline-edit-input"
                                                                        value={hotelEditForm.latitude}
                                                                        onChange={handleHotelEditFieldChange}
                                                                        aria-label="Hotel latitude"
                                                                        placeholder="Latitude"
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        step="any"
                                                                        name="longitude"
                                                                        className="hotel-inline-edit-input"
                                                                        value={hotelEditForm.longitude}
                                                                        onChange={handleHotelEditFieldChange}
                                                                        aria-label="Hotel longitude"
                                                                        placeholder="Longitude"
                                                                    />
                                                                    <div className="hotel-inline-edit-actions">
                                                                        <button type="submit" className="hotel-icon-action" title="Save hotel" disabled={editSubmitState.loading}>
                                                                            <i className="bi bi-check-lg"></i>
                                                                        </button>
                                                                        <button type="button" className="hotel-icon-action" title="Cancel" onClick={() => setEditingHotelId(null)}>
                                                                            <i className="bi bi-x-lg"></i>
                                                                        </button>
                                                                    </div>
                                                                    {editSubmitState.error && <div className="hotel-inline-edit-message is-error">{editSubmitState.error}</div>}
                                                                </form>
                                                            </td>
                                                        </tr>
                                                    );
                                                }

                                                return (
                                                    <tr
                                                        key={hotelOptionId}
                                                        className={isCurrentHotel ? 'is-current' : ''}
                                                    >
                                                        <td>
                                                            <div className="hotel-switcher-item-name">{hotelOption.DHTHOT_name}</div>
                                                            <div className="hotel-switcher-item-meta">
                                                                Hotel ID {hotelOption.DHTHOTid}
                                                            </div>
                                                        </td>
                                                        <td className="hotel-switcher-cell-muted">
                                                            {formatCoordinate(hotelOption.DHTHOT_latitude)}
                                                        </td>
                                                        <td className="hotel-switcher-cell-muted">
                                                            {formatCoordinate(hotelOption.DHTHOT_longitude)}
                                                        </td>
                                                        <td className="hotel-switcher-action-col">
                                                            <button
                                                                type="button"
                                                                className={`hotel-switcher-item-action ${isCurrentHotel ? 'is-current' : ''}`}
                                                                onClick={() => handleHotelSelect(hotelOptionId)}
                                                            >
                                                                {isCurrentHotel ? 'Current' : 'Open'}
                                                            </button>
                                                            {role === 'admin' && (
                                                                <button
                                                                    type="button"
                                                                    className="hotel-icon-action"
                                                                    title="Edit hotel"
                                                                    onClick={() => startHotelEdit(hotelOption)}
                                                                >
                                                                    <i className="bi bi-pencil"></i>
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="hotel-switcher-empty">
                                    No hotels available right now.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {showAvailabilityCalendar && (
                <div className="hotel-switcher-backdrop" onClick={() => setShowAvailabilityCalendar(false)}>
                    <div
                        className="hotel-switcher-window availability-calendar-window"
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="availability-calendar-title"
                    >
                        <div className="hotel-switcher-header">
                            <div>
                                <div className="hotel-switcher-eyebrow">Box Availability</div>
                                <h2 id="availability-calendar-title" className="hotel-switcher-title mb-0">
                                    {calendarMonthLabel}
                                </h2>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    type="button"
                                    className="hotel-sidebar-calendar-control"
                                    onClick={() => handleCalendarMonthChange(-1)}
                                    aria-label="Previous month"
                                >
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                                <button
                                    type="button"
                                    className="hotel-sidebar-calendar-control"
                                    onClick={() => handleCalendarMonthChange(1)}
                                    aria-label="Next month"
                                >
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-light rounded-pill px-3"
                                    onClick={() => setShowAvailabilityCalendar(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>

                        <div className="hotel-switcher-list">
                            <div className="availability-calendar-card">
                                <div className="hotel-sidebar-calendar-summary">
                                    <span>{todayAvailability.availableCount}</span>
                                    available today
                                    <strong>{todayAvailability.reservedCount}</strong>
                                    reserved today
                                </div>

                                <div className="availability-box-filter">
                                    <div className="availability-box-filter-title">
                                        Filter boxes
                                    </div>

                                    <div className="availability-box-filter-list">
                                        {boxes.map((box) => {
                                            const boxIdStr = String(box.DHTBOX_id);
                                            const checked = selectedCalendarBoxIds.has(boxIdStr);

                                            return (
                                                <label
                                                    key={boxIdStr}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.45rem',
                                                        padding: '0.15rem 0.1rem',
                                                        cursor: 'pointer',
                                                        color: checked ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.75)'
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={(e) => {
                                                            const next = new Set(selectedCalendarBoxIds);
                                                            if (e.target.checked) next.add(boxIdStr);
                                                            else next.delete(boxIdStr);
                                                            setSelectedCalendarBoxIds(next);
                                                        }}
                                                    />
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                        <span
                                                            style={{
                                                                width: '10px',
                                                                height: '10px',
                                                                borderRadius: '50%',
                                                                background: normalizedBoxes.find((b) => String(b.DHTBOX_id) === boxIdStr)?._boxColor || '#94a3b8',
                                                                display: 'inline-block'
                                                            }}
                                                        />
                                                        Box {box.DHTBOX_id}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>

                                    <div className="availability-box-filter-actions">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-light rounded-pill px-3"
                                            onClick={() => setSelectedCalendarBoxIds(new Set(boxes.map((b) => String(b.DHTBOX_id))))}
                                        >
                                            Select all
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-light rounded-pill px-3"
                                            onClick={() => setSelectedCalendarBoxIds(new Set())}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>


                                <div className="hotel-sidebar-calendar-grid" aria-label="Box availability calendar">
                                    {CALENDAR_WEEKDAYS.map((weekday) => (
                                        <div key={weekday} className="hotel-sidebar-calendar-weekday">
                                            {weekday}
                                        </div>
                                    ))}
                                    {calendarDays.map((day) => {
                                        const dayAvailability = calendarAvailability[day.key] || {
                                            availableCount: 0,
                                            reservedCount: 0,
                                            availableBoxes: [],
                                            reservedBoxes: [],
                                            allReserved: false
                                        };
                                        const hasAvailability = dayAvailability.availableCount > 0;
                                        const hasReservations = dayAvailability.reservedCount > 0;
                                        const visibleAvailableBoxIds = dayAvailability.availableBoxes
                                            .slice(0, 3)
                                            .map((box) => `Box ${box.DHTBOX_id}`)
                                            .join(', ');
                                        const visibleReservedBoxIds = dayAvailability.reservedBoxes
                                            .slice(0, 3)
                                            .map((box) => `Box ${box.DHTBOX_id}`)
                                            .join(', ');
                                        const dayTitle = [
                                            hasAvailability ? `Available: ${visibleAvailableBoxIds}${dayAvailability.availableCount > 3 ? '...' : ''}` : 'Available: none',
                                            hasReservations ? `Reserved: ${visibleReservedBoxIds}${dayAvailability.reservedCount > 3 ? '...' : ''}` : 'Reserved: none'
                                        ].join(' | ');

                                        return (
                                            <button
                                                key={day.key}
                                                type="button"
                                                className={[
                                                    'hotel-sidebar-calendar-day',
                                                    day.isCurrentMonth ? '' : 'is-muted',
                                                    day.isToday ? 'is-today' : '',
                                                    dayAvailability.allReserved ? 'is-all-reserved' : '',
                                                    hasAvailability ? 'has-availability' : 'is-full',
                                                    hasReservations ? 'has-reservations' : ''
                                                ].filter(Boolean).join(' ')}
                                                title={dayTitle}
                                                onClick={() => setSelectedAvailabilityDay(getStartOfDay(day.date))}
                                            >
                                                <div className="calendar-day-number">
                                                {day.dayNumber}
                                                </div>

                                                <div className="calendar-boxes">

                                                {dayAvailability.availableBoxes?.map(box => (
                                                    <div
                                                        key={`available-${box.DHTBOX_id}`}
                                                        className="calendar-box available"
                                                        style={{ background: box._boxColor, borderColor: 'rgba(255,255,255,0.35)' }}
                                                    >
                                                    Box {box.DHTBOX_id}
                                                    </div>
                                                ))}

                                                {dayAvailability.reservedBoxes?.map(box => (
                                                    <div
                                                        key={`reserved-${box.DHTBOX_id}`}
                                                        className="calendar-box reserved"
                                                        style={{ background: box._boxColor, borderColor: 'rgba(255,255,255,0.35)' }}
                                                    >
                                                    Box {box.DHTBOX_id}
                                                    </div>
                                                ))}

                                                </div>

                                            </button>
                                        );
                                    })}

                                </div>

                                <div className="hotel-sidebar-calendar-legend">
                                    <span><i className="legend-dot available"></i> Available</span>
                                    <span><i className="legend-dot reserved"></i> Reserved</span>
                                    <span><i className="legend-dot full"></i> Full</span>
                                </div>

                                <div className="day-view-grid availability-day-view">
                                    <div className="day-view-header-row">
                                        <div>Time</div>
                                        <div>
                                            {selectedAvailabilityDay ? (
                                                <span style={{ color: 'rgba(226, 232, 240, 0.9)', fontWeight: 900 }}>
                                                    {selectedAvailabilityDay.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: '2-digit' })}
                                                </span>
                                            ) : (
                                                <span style={{ color: 'rgba(226, 232, 240, 0.7)', fontWeight: 800 }}>
                                                    Click a day to see hours
                                                </span>
                                            )}
                                        </div>

                                    </div>

                                            {selectedAvailabilityDay ? (
                                    (() => {
                                            const hoursSchedule = getHourScheduleForDay({
                                                reservationsForHotel: hotelReservations,
                                                dayDate: selectedAvailabilityDay,
                                                allBoxes: filteredNormalizedBoxes
                                            });


                                            return hoursSchedule.map((row) => (
                                                <div
                                                    key={row.hour}
                                                    className={[
                                                        'day-view-row',
                                                        row.reservedCount > 0 ? 'is-reserved' : 'is-available'
                                                    ].filter(Boolean).join(' ')}
                                                >
                                                    <div className="day-view-time-col">
                                                        {String(row.hour).padStart(2, '0')}:00
                                                    </div>
                                                    <div className="day-view-box-col">
                                                        {row.reservedCount > 0 ? (
                                                            <div className="day-view-slot-boxes">
                                                                {row.reservedBoxes.slice(0, 10).map((b) => {
                                                                    const matchingReservation = row.reservationsForHour?.find(
                                                                        (r) => String(getReservationBoxId(r)) === String(b.DHTBOX_id)
                                                                    );
                                                                    const startValue = matchingReservation?.DHTRSV_start_Date || matchingReservation?.startTime || matchingReservation?.start_date;
                                                                    const endValue = matchingReservation?.DHTRSV_end_Date || matchingReservation?.endTime || matchingReservation?.end_date;
                                                                    const title = matchingReservation
                                                                        ? `${new Date(startValue).toLocaleString()} - ${new Date(endValue).toLocaleString()}`
                                                                        : 'Reserved';

                                                                    return (
                                                                        <span
                                                                            key={b.DHTBOX_id}
                                                                            className="day-view-box reserved"
                                                                            style={{ background: b._boxColor }}
                                                                            title={title}
                                                                        >
                                                                            Box {b.DHTBOX_id}
                                                                        </span>
                                                                    );
                                                                })}
                                                                {row.reservedBoxes.length > 10 && (
                                                                    <span className="day-view-empty">+{row.reservedBoxes.length - 10} more</span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="day-view-empty">No reservations</div>
                                                        )}

                                                    </div>
                                                </div>
                                            ));
                                        })()
                                    ) : (
                                        <div className="availability-day-view-empty">
                                            Select a day from the calendar to see available hours.
                                        </div>
                                    )}

                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showComponentsWindow && (
                <div className="hotel-switcher-backdrop" onClick={() => setShowComponentsWindow(false)}>
                    <div
                        className="hotel-switcher-window hotel-components-window"
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="hotel-components-title"
                    >
                        <div className="hotel-switcher-header">
                            <div>
                                <div className="hotel-switcher-eyebrow">Components</div>
                                <h2 id="hotel-components-title" className="hotel-switcher-title mb-0">
                                    {selectedHotelName} components
                                </h2>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-light rounded-pill px-3"
                                onClick={() => setShowComponentsWindow(false)}
                            >
                                Close
                            </button>
                        </div>

                        <div className="hotel-switcher-list">
                            {components.length > 0 ? (
                                <div className="hotel-switcher-table-wrap">
                                    <table className="hotel-switcher-table">
                                        <thead>
                                            <tr>
                                                <th>Component ID</th>
                                                <th>Type</th>
                                                <th>Place</th>
                                                <th>Box ID</th>
                                                <th>Added</th>
                                                {role === 'admin' && <th className="hotel-switcher-action-col">Edit</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {components.map((component, index) => {
                                                const componentId = getComponentId(component);
                                                const isEditingComponent = String(editingComponentId) === String(componentId);
                                                const rowKey = [
                                                    componentId,
                                                    component.DHTBOX_id || 'no-box',
                                                    component.component_type || 'no-type',
                                                    component.component_place || 'no-place',
                                                    component.DHTCMP_entry_Date || 'no-date',
                                                    index
                                                ].join('-');

                                                if (isEditingComponent) {
                                                    return (
                                                        <tr key={rowKey}>
                                                            <td colSpan={role === 'admin' ? 6 : 5}>
                                                                <form onSubmit={saveComponentEdit} className="hotel-inline-edit-form">
                                                                    <span className="hotel-switcher-stat">
                                                                        <i className="bi bi-cpu me-2"></i>
                                                                        {componentId}
                                                                    </span>
                                                                    <input
                                                                        type="text"
                                                                        name="component_type"
                                                                        className="hotel-inline-edit-input"
                                                                        value={componentEditForm.component_type}
                                                                        onChange={handleComponentEditFieldChange}
                                                                        aria-label="Component type"
                                                                        required
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        name="component_place"
                                                                        className="hotel-inline-edit-input"
                                                                        value={componentEditForm.component_place}
                                                                        onChange={handleComponentEditFieldChange}
                                                                        aria-label="Component place"
                                                                        required
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        name="DHTBOX_id"
                                                                        className="hotel-inline-edit-input"
                                                                        value={componentEditForm.DHTBOX_id}
                                                                        onChange={handleComponentEditFieldChange}
                                                                        aria-label="Linked box ID"
                                                                        placeholder="Box ID"
                                                                    />
                                                                    <input
                                                                        type="datetime-local"
                                                                        name="DHTCMP_entry_Date"
                                                                        className="hotel-inline-edit-input is-wide"
                                                                        value={componentEditForm.DHTCMP_entry_Date}
                                                                        onChange={handleComponentEditFieldChange}
                                                                        aria-label="Component entry date"
                                                                    />
                                                                    <div className="hotel-inline-edit-actions">
                                                                        <button type="submit" className="hotel-icon-action" title="Save component" disabled={editSubmitState.loading}>
                                                                            <i className="bi bi-check-lg"></i>
                                                                        </button>
                                                                        <button type="button" className="hotel-icon-action" title="Cancel" onClick={() => setEditingComponentId(null)}>
                                                                            <i className="bi bi-x-lg"></i>
                                                                        </button>
                                                                    </div>
                                                                    {editSubmitState.error && <div className="hotel-inline-edit-message is-error">{editSubmitState.error}</div>}
                                                                </form>
                                                            </td>
                                                        </tr>
                                                    );
                                                }

                                                return (
                                                    <tr key={rowKey}>
                                                        <td>
                                                            <span className="hotel-switcher-stat">
                                                                <i className="bi bi-cpu me-2"></i>
                                                                {component.DHTCMP_id}
                                                            </span>
                                                        </td>
                                                        <td>{component.component_type || 'Unknown type'}</td>
                                                        <td className="hotel-switcher-cell-muted">
                                                            {component.component_place || 'Unknown place'}
                                                        </td>
                                                        <td>{component.DHTBOX_id || 'Not linked'}</td>
                                                        <td className="hotel-switcher-cell-muted">
                                                            {component.DHTCMP_entry_Date
                                                                ? new Date(component.DHTCMP_entry_Date).toLocaleDateString()
                                                                : 'Unknown'}
                                                        </td>
                                                        {role === 'admin' && (
                                                            <td className="hotel-switcher-action-col">
                                                                <button
                                                                    type="button"
                                                                    className="hotel-icon-action"
                                                                    title="Edit component"
                                                                    onClick={() => startComponentEdit(component)}
                                                                >
                                                                    <i className="bi bi-pencil"></i>
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-white opacity-50 p-5 text-center border border-dashed rounded-3" style={{ borderStyle: 'dashed !important' }}>
                                    <i className="bi bi-cpu fs-1 d-block mb-2"></i>
                                    No components found for this hotel.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {showFlightPlanWindow && (
                <div className="hotel-switcher-backdrop" onClick={() => setShowFlightPlanWindow(false)}>
                    <div
                        className="hotel-switcher-window"
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="flight-plan-title"
                    >
                        <div className="hotel-switcher-header">
                            <div>
                                <div className="hotel-switcher-eyebrow">Flight Plan</div>
                                <h2 id="flight-plan-title" className="hotel-switcher-title mb-0">{selectedHotelName} flight planning</h2>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-light rounded-pill px-3"
                                onClick={() => setShowFlightPlanWindow(false)}
                            >
                                Close
                            </button>
                        </div>

                        <div className="hotel-switcher-list">
                            <div className="hotel-box-card text-white h-100">
                                <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                                    <div>
                                        <div className="small text-uppercase opacity-75 mb-2">Route Summary</div>
                                        <h4 className="mb-1">{hotel?.DHTHOT_name || 'Unknown Hotel'}</h4>
                                        <p className="text-white-50 mb-0">
                                            Use this window for flight planning details, route checkpoints, or dispatch notes.
                                        </p>
                                    </div>
                                    <span className="hotel-box-chip">
                                        <i className="bi bi-send me-2"></i>
                                        Pending Setup
                                    </span>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <div className="hotel-info-badge py-3 px-3 shadow-lg w-100 h-100">
                                            <div className="small text-muted text-uppercase mb-2">Hotel</div>
                                            <div className="fw-bold" style={{ color: '#1e293b' }}>{hotel?.DHTHOT_name || 'Unknown Hotel'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="hotel-info-badge py-3 px-3 shadow-lg w-100 h-100">
                                            <div className="small text-muted text-uppercase mb-2">Location</div>
                                            <div className="fw-bold" style={{ color: '#1e293b' }}>{hotel?.DHTHOT_location || 'Unknown'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="hotel-info-badge py-3 px-3 shadow-lg w-100 h-100">
                                            <div className="small text-muted text-uppercase mb-2">Active Boxes</div>
                                            <div className="fw-bold" style={{ color: '#1e293b' }}>{boxes.length}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
	            {showBoxDetailModal && selectedBoxForDetailLive && (
	                <div
	                    className="hotel-switcher-backdrop box-detail-backdrop"
	                    onClick={() => {
	                        setShowBoxDetailModal(false);
	                        setEditingBoxId(null);
	                    }}
	                >
                    <div
                        className="hotel-switcher-window box-detail-window"
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="box-detail-title"
                    >
                        <div className="hotel-switcher-header">
                            <div>
                                <div className="hotel-switcher-eyebrow">Box Details</div>
                                <h2 id="box-detail-title" className="hotel-switcher-title mb-0">
                                    Box {selectedBoxForDetailLive.DHTBOX_id}
                                </h2>
                            </div>
	                            <button
	                                type="button"
	                                className="btn btn-sm btn-outline-light rounded-pill px-3"
	                                onClick={() => {
	                                    setShowBoxDetailModal(false);
	                                    setEditingBoxId(null);
	                                }}
	                            >
                                Close
                            </button>
                        </div>

                        <div className="hotel-switcher-list">
                            <div className="text-white">
	                                <div className="mb-4 pb-4 border-bottom border-secondary">
	                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
	                                        <h4 className="mb-0">Box Information</h4>
	                                        {role === 'admin' && (
	                                            <button
	                                                type="button"
	                                                className="hotel-icon-action"
	                                                title={`Edit box ${selectedBoxForDetailLive.DHTBOX_id}`}
	                                                onClick={(event) => startBoxEdit(selectedBoxForDetailLive, event)}
	                                            >
	                                                <i className="bi bi-pencil"></i>
	                                            </button>
	                                        )}
	                                    </div>
	                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <div className="hotel-info-badge py-3 px-3 shadow-lg w-100">
                                                <div className="small text-muted text-uppercase mb-2">Box ID</div>
                                                <div className="fw-bold" style={{ color: '#1e293b' }}>{selectedBoxForDetailLive.DHTBOX_id}</div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="hotel-info-badge py-3 px-3 shadow-lg w-100">
                                                <div className="small text-muted text-uppercase mb-2">Status</div>
                                                <div className="fw-bold" style={{ color: '#1e293b' }}>
                                                    {selectedBoxForDetailLive._liveStatusLabel || 'Unknown'}
	                                        </div>
	                                    </div>
	                                    {role === 'admin' && String(editingBoxId) === String(selectedBoxForDetailLive.DHTBOX_id) && (
	                                        <form onSubmit={saveBoxEdit} className="hotel-box-edit-panel">
	                                            <div className="hotel-box-edit-panel-title">Edit Box {editingBoxId}</div>
	                                            <input
	                                                type="number"
	                                                name="DHTHOT_id"
	                                                className="hotel-inline-edit-input"
	                                                value={boxEditForm.DHTHOT_id}
	                                                onChange={handleBoxEditFieldChange}
	                                                aria-label="Hotel ID"
	                                                required
	                                            />
	                                            <input
	                                                type="number"
	                                                name="DHTBOX_position"
	                                                className="hotel-inline-edit-input"
	                                                value={boxEditForm.DHTBOX_position}
	                                                onChange={handleBoxEditFieldChange}
	                                                aria-label="Box position"
	                                                required
	                                            />
	                                            <input
	                                                type="number"
	                                                name="DHTBOX_length_mm"
	                                                className="hotel-inline-edit-input"
	                                                value={boxEditForm.DHTBOX_length_mm}
	                                                onChange={handleBoxEditFieldChange}
	                                                aria-label="Box length"
	                                                placeholder="Length"
	                                                required
	                                            />
	                                            <input
	                                                type="number"
	                                                name="DHTBOX_width_mm"
	                                                className="hotel-inline-edit-input"
	                                                value={boxEditForm.DHTBOX_width_mm}
	                                                onChange={handleBoxEditFieldChange}
	                                                aria-label="Box width"
	                                                placeholder="Width"
	                                                required
	                                            />
	                                            <input
	                                                type="number"
	                                                name="DHTBOX_height_mm"
	                                                className="hotel-inline-edit-input"
	                                                value={boxEditForm.DHTBOX_height_mm}
	                                                onChange={handleBoxEditFieldChange}
	                                                aria-label="Box height"
	                                                placeholder="Height"
	                                                required
	                                            />
	                                            <input
	                                                type="text"
	                                                name="DHTBOX_qr_code"
	                                                className="hotel-inline-edit-input is-wide"
	                                                value={boxEditForm.DHTBOX_qr_code}
	                                                onChange={handleBoxEditFieldChange}
	                                                aria-label="Box QR code"
	                                                placeholder="QR code"
	                                                required
	                                            />
	                                            <div className="hotel-inline-edit-actions">
	                                                <button type="submit" className="hotel-icon-action" title="Save box" disabled={editSubmitState.loading}>
	                                                    <i className="bi bi-check-lg"></i>
	                                                </button>
	                                                <button type="button" className="hotel-icon-action" title="Cancel" onClick={() => setEditingBoxId(null)}>
	                                                    <i className="bi bi-x-lg"></i>
	                                                </button>
	                                            </div>
	                                            {editSubmitState.error && <div className="hotel-inline-edit-message is-error">{editSubmitState.error}</div>}
	                                        </form>
	                                    )}
	                                </div>
                                        <div className="col-md-6">
                                            <div className="hotel-info-badge py-3 px-3 shadow-lg w-100">
                                                <div className="small text-muted text-uppercase mb-2">Dimensions (mm)</div>
                                                <div className="fw-bold" style={{ color: '#1e293b' }}>
                                                    {selectedBoxForDetailLive.DHTBOX_length_mm || 'N/A'} × {selectedBoxForDetailLive.DHTBOX_width_mm || 'N/A'} × {selectedBoxForDetailLive.DHTBOX_height_mm || 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="hotel-info-badge py-3 px-3 shadow-lg w-100">
                                                <div className="small text-muted text-uppercase mb-2">Position</div>
                                                <div className="fw-bold" style={{ color: '#1e293b' }}>
                                                    {selectedBoxForDetailLive.DHTBOX_position || 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4 pb-4 border-bottom border-secondary">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                                        <h4 className="mb-0">Availability & Reservation</h4>
                                    </div>

                                    {selectedBoxAvailability.activeReservation ? (
                                        <div className="box-reservation-note mb-3">
                                            <strong>Current reservation:</strong>{' '}
                                            {new Date(getReservationStartValue(selectedBoxAvailability.activeReservation)).toLocaleString()} - {new Date(getReservationEndValue(selectedBoxAvailability.activeReservation)).toLocaleString()}
                                        </div>
                                    ) : (
                                        <div className="box-reservation-note mb-3">
                                            This box is available for a new reservation if your selected time does not overlap an upcoming booking.
                                        </div>
                                    )}

                                    {selectedBoxAvailability.upcomingReservations.length > 0 && (
                                        <div className="box-upcoming-reservations mb-3">
                                            <div className="small text-white-50 text-uppercase fw-bold mb-2">Upcoming bookings</div>
                                            {selectedBoxAvailability.upcomingReservations.slice(0, 4).map((reservation) => (
                                                <div key={reservation.DHTRSV_id || `${getReservationStartValue(reservation)}-${getReservationEndValue(reservation)}`} className="box-upcoming-reservation-row">
                                                    <span>{new Date(getReservationStartValue(reservation)).toLocaleString()}</span>
                                                    <span>{new Date(getReservationEndValue(reservation)).toLocaleString()}</span>
                                                </div>
                                            ))}
                                            {selectedBoxAvailability.upcomingReservations.length > 4 && (
                                                <div className="text-white-50 small mt-1">
                                                    +{selectedBoxAvailability.upcomingReservations.length - 4} more
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="box-detail-calendar-card mb-3">
                                        <div className="box-detail-calendar-header">
                                            <div>
                                                <div className="small text-white-50 text-uppercase fw-bold">Calendar</div>
                                                <div className="box-detail-calendar-title">{calendarMonthLabel}</div>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <button
                                                    type="button"
                                                    className="hotel-sidebar-calendar-control"
                                                    onClick={() => handleCalendarMonthChange(-1)}
                                                    aria-label="Previous month"
                                                >
                                                    <i className="bi bi-chevron-left"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="hotel-sidebar-calendar-control"
                                                    onClick={() => handleCalendarMonthChange(1)}
                                                    aria-label="Next month"
                                                >
                                                    <i className="bi bi-chevron-right"></i>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="box-detail-calendar-grid" aria-label={`Availability calendar for box ${selectedBoxForDetailLive.DHTBOX_id}`}>
                                            {CALENDAR_WEEKDAYS.map((weekday) => (
                                                <div key={weekday} className="hotel-sidebar-calendar-weekday">
                                                    {weekday}
                                                </div>
                                            ))}
                                            {calendarDays.map((day) => {
                                                const dayAvailability = selectedBoxCalendarAvailability[day.key] || {
                                                    isAvailable: false,
                                                    isReserved: false,
                                                    reservations: []
                                                };
                                                const dayDate = getStartOfDay(day.date);
                                                const isSelectedForReservation = selectedBoxReservationDateRange
                                                    ? dayDate >= selectedBoxReservationDateRange.startDate && dayDate <= selectedBoxReservationDateRange.endDate
                                                    : false;

                                                return (
                                                    <button
                                                        key={day.key}
                                                        type="button"
                                                        className={[
                                                            'box-detail-calendar-day',
                                                            day.isCurrentMonth ? '' : 'is-muted',
                                                            day.isToday ? 'is-today' : '',
                                                            dayAvailability.isAvailable ? 'is-available' : '',
                                                            dayAvailability.isReserved ? 'is-reserved' : '',
                                                            isSelectedForReservation ? 'is-selected-for-reservation' : '',
                                                            boxReservationSelection.isDragging && isSelectedForReservation ? 'is-dragging-selection' : ''
                                                        ].filter(Boolean).join(' ')}
                                                        onPointerDown={(event) => startBoxCalendarSelection(event, day.date)}
                                                        onPointerEnter={() => updateBoxCalendarSelection(day.date)}
                                                        onPointerUp={finishBoxCalendarSelection}
                                                        title={dayAvailability.isReserved ? 'Reserved on this day' : 'Available on this day'}
                                                    >
                                                        <span>{day.dayNumber}</span>
                                                        <small>{dayAvailability.isReserved ? 'Reserved' : dayAvailability.isAvailable ? 'Available' : 'Unavailable'}</small>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="box-detail-reserver">
                                        <BoxReserver
                                            userId="1"
                                            selectedBoxId={selectedBoxForDetailLive.DHTBOX_id}
                                            lockBoxSelection
                                            initialStartTime={selectedBoxReservationDateRange?.startTime || ''}
                                            initialEndTime={selectedBoxReservationDateRange?.endTime || ''}
                                            onReservationSuccess={fetchReservations}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <h4 className="mb-3">Components Inside This Box</h4>
                                    {getBoxComponents(selectedBoxForDetailLive.DHTBOX_id).length > 0 ? (
                                        <div className="hotel-switcher-table-wrap">
                                            <table className="hotel-switcher-table">
                                                <thead>
                                                    <tr>
                                                        <th>Component ID</th>
                                                        <th>Type</th>
                                                        <th>Place</th>
                                                        <th>Added</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getBoxComponents(selectedBoxForDetailLive.DHTBOX_id).map((component) => (
                                                        <tr key={component.DHTCMP_id}>
                                                            <td>
                                                                <span className="hotel-switcher-stat">
                                                                    <i className="bi bi-cpu me-2"></i>
                                                                    {component.DHTCMP_id}
                                                                </span>
                                                            </td>
                                                            <td>{component.component_type || 'Unknown type'}</td>
                                                            <td className="hotel-switcher-cell-muted">
                                                                {component.component_place || 'Unknown place'}
                                                            </td>
                                                            <td className="hotel-switcher-cell-muted">
                                                                {component.DHTCMP_entry_Date
                                                                    ? new Date(component.DHTCMP_entry_Date).toLocaleDateString()
                                                                    : 'Unknown'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-white opacity-50 p-4 text-center border border-dashed rounded-3 bg-dark bg-opacity-25">
                                            <i className="bi bi-inbox fs-4 d-block mb-2"></i>
                                            <p className="mb-0">No components found in this box</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <style>
                {`
                    .hover-up:hover {
                        transform: translateY(-5px);
                        background: rgba(255, 255, 255, 0.1) !important;
                    }
                    .transition-all {
                        transition: all 0.3s ease;
                    }
                    .transition-all:hover {
                        background: rgba(255, 255, 255, 0.1);
                        padding-left: 3.5rem !important;
                        padding-right: 3.5rem !important;
                    }
                    .hotel-box-slot {
                        cursor: pointer;
                        transition: all 0.2s ease;
                    }
                    .hotel-box-slot:hover {
                        transform: scale(1.05);
                        box-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
                    }
                `}
            </style>
        </div>
    );
};

export default HotelPage;
